import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldAlert, Globe as GlobeIcon, Ban, Radar, Pause, Play, Volume2, VolumeX, Maximize, Minimize } from "lucide-react";
import { Globe, SOURCES } from "@/components/site/Globe";
import { Reveal, SectionLabel } from "@/components/site/primitives";
import { useI18n } from "@/i18n";

const COUNTRIES = [
  { c: "Rusia", c_en: "Russia", code: "RU", base: 38 },
  { c: "China", c_en: "China", code: "CN", base: 27 },
  { c: "S.U.A.", c_en: "U.S.A.", code: "US", base: 14 },
  { c: "Brazilia", c_en: "Brazil", code: "BR", base: 11 },
  { c: "India", c_en: "India", code: "IN", base: 10 },
];

const PORTS = [
  { p: "22", s: "SSH" },
  { p: "3389", s: "RDP" },
  { p: "443", s: "HTTPS" },
  { p: "445", s: "SMB" },
  { p: "8080", s: "HTTP-alt" },
];

const LiveFeed = ({ paused, soundOn, onTogglePause, onToggleSound, audioRef }) => {
  const [items, setItems] = useState([]);
  const { t } = useI18n();
  const pausedRef = useRef(paused);
  const soundRef = useRef(soundOn);
  useEffect(() => { pausedRef.current = paused; }, [paused]);
  useEffect(() => { soundRef.current = soundOn; }, [soundOn]);

  const beep = () => {
    if (!soundRef.current) return;
    const ac = audioRef.current;
    if (!ac) return;
    try {
      if (ac.state === "suspended") ac.resume();
      const o = ac.createOscillator();
      const g = ac.createGain();
      o.type = "sine";
      o.frequency.setValueAtTime(920, ac.currentTime);
      g.gain.setValueAtTime(0.0001, ac.currentTime);
      g.gain.exponentialRampToValueAtTime(0.045, ac.currentTime + 0.012);
      g.gain.exponentialRampToValueAtTime(0.0001, ac.currentTime + 0.14);
      o.connect(g);
      g.connect(ac.destination);
      o.start();
      o.stop(ac.currentTime + 0.16);
    } catch (e) { /* noop */ }
  };

  useEffect(() => {
    let n = 0;
    const id = setInterval(() => {
      if (pausedRef.current) return;
      const src = SOURCES[Math.floor(Math.random() * SOURCES.length)];
      const item = { id: ++n, src };
      setItems((p) => [item, ...p].slice(0, 3));
      beep();
      setTimeout(() => setItems((p) => p.filter((x) => x.id !== item.id)), 4800);
    }, 2800);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ctrlCls =
    "pointer-events-auto grid h-8 w-8 place-items-center rounded-md border border-white/10 bg-ink-900/80 text-white/70 backdrop-blur-md transition-colors duration-200 hover:border-cyan-neon/50 hover:text-cyan-neon";

  return (
    <>
      {/* Controls (top-right) */}
      <div className="absolute right-3 top-3 z-20 flex items-center gap-1.5">
        <button
          data-testid="feed-pause"
          onClick={onTogglePause}
          aria-label={paused ? "Reia feed-ul" : "Pauză feed"}
          className={ctrlCls}
        >
          {paused ? <Play size={14} /> : <Pause size={14} />}
        </button>
        <button
          data-testid="feed-sound"
          onClick={onToggleSound}
          aria-label={soundOn ? "Oprește sunetul" : "Pornește sunetul"}
          className={`${ctrlCls} ${soundOn ? "border-cyan-neon/50 text-cyan-neon" : ""}`}
        >
          {soundOn ? <Volume2 size={14} /> : <VolumeX size={14} />}
        </button>
        <span className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1"
          style={{ borderColor: paused ? "#f59e0b55" : "#00E5FF55", background: paused ? "#f59e0b18" : "#00E5FF18" }}>
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: paused ? "#f59e0b" : "#00E5FF", boxShadow: `0 0 8px ${paused ? "#f59e0b" : "#00E5FF"}`, animation: paused ? "none" : "pulse 2s infinite" }} />
          <span className="font-mono text-[9px] uppercase tracking-widest" style={{ color: paused ? "#f59e0b" : "#00E5FF" }}>{paused ? t("am.paused") : "pfSense"}</span>
        </span>
      </div>

      {/* Feed banners (top-left) */}
      <div className="pointer-events-none absolute left-3 top-14 z-10 flex w-[230px] flex-col gap-2 sm:top-16">
        <AnimatePresence initial={false}>
          {items.map((it) => (
            <motion.div
              key={it.id}
              layout
              initial={{ opacity: 0, x: -22, scale: 0.94 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: -22, scale: 0.9 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="rounded-lg border border-[#ff3b6b]/30 bg-ink-900/90 px-3 py-2 shadow-[0_6px_22px_rgba(0,0,0,0.45)] backdrop-blur-md"
            >
              <div className="flex items-center gap-1.5">
                <span className="text-sm leading-none">{it.src.flag}</span>
                <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#ff7a97]">{t("am.attackblocked")}</span>
                <span className="ml-auto rounded bg-emerald-400/15 px-1.5 py-0.5 font-mono text-[8px] font-bold text-emerald-300">{t("am.blocked")}</span>
              </div>
              <div className="mt-1 font-display text-[12px] font-semibold leading-tight text-white">
                {it.src.city}, {it.src.code}
              </div>
              <div className="truncate font-mono text-[9px] text-white/45">{it.src.isp} · {it.src.atk}</div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </>
  );
};

const LiveDot = ({ color = "#ff3b6b", label }) => {
  const { t } = useI18n();
  const lbl = label || t("am.live");
  return (
  <span className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1"
    style={{ borderColor: `${color}55`, background: `${color}18` }}>
    <span className="h-1.5 w-1.5 animate-pulse rounded-full" style={{ background: color, boxShadow: `0 0 8px ${color}` }} />
    <span className="font-mono text-[9px] uppercase tracking-widest" style={{ color }}>{lbl}</span>
  </span>
  );
};

const Panel = ({ title, icon: Icon, accent = "#00E5FF", right, children }) => (
  <div className="card-edge rounded-xl p-5">
    <div className="mb-4 flex items-center justify-between">
      <div className="flex items-center gap-2.5">
        <span className="grid h-8 w-8 place-items-center rounded-md border border-white/10" style={{ color: accent }}>
          <Icon size={16} />
        </span>
        <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/60">{title}</span>
      </div>
      {right}
    </div>
    {children}
  </div>
);

export const AttackMap = () => {
  const sectionRef = useRef(null);
  const audioRef = useRef(null);
  const { t, lang } = useI18n();
  const [isFull, setIsFull] = useState(false);
  const [feedPaused, setFeedPaused] = useState(false);
  const [feedSound, setFeedSound] = useState(false);
  const [tourCity, setTourCity] = useState(null);
  const [blocked, setBlocked] = useState(48213);
  const [threats, setThreats] = useState(6);
  const [ports, setPorts] = useState(() => PORTS.map((p) => ({ ...p, n: 200 + Math.floor(Math.random() * 900) })));
  const [countries, setCountries] = useState(() => COUNTRIES.map((c) => ({ ...c, v: c.base + Math.random() * 4 })));

  useEffect(() => {
    const id = setInterval(() => {
      setBlocked((b) => b + Math.floor(Math.random() * 9) + 1);
      setThreats((t) => Math.max(0, Math.min(12, t + (Math.random() > 0.6 ? 1 : Math.random() > 0.55 ? -1 : 0))));
      setPorts((ps) => ps.map((p) => ({ ...p, n: Math.max(80, p.n + Math.floor((Math.random() - 0.4) * 40)) })));
      setCountries((cs) => cs.map((c) => ({ ...c, v: Math.max(4, Math.min(60, c.base + (Math.random() - 0.5) * 8)) })));
    }, 1400);
    return () => clearInterval(id);
  }, []);

  const ensureAudio = () => {
    if (!audioRef.current) {
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (Ctx) audioRef.current = new Ctx();
      } catch (e) { /* noop */ }
    }
    if (audioRef.current && audioRef.current.state === "suspended") audioRef.current.resume();
  };

  useEffect(() => {
    const onFs = () => {
      const full = document.fullscreenElement === sectionRef.current;
      setIsFull(full);
      if (full) {
        // Auto-play hands-free demo: unpause feed + enable discreet sound
        setFeedPaused(false);
        ensureAudio();
        setFeedSound(true);
      }
    };
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  const togglePresent = () => {
    const el = sectionRef.current;
    if (!document.fullscreenElement) {
      ensureAudio(); // create/resume within the user click gesture
      el?.requestFullscreen?.().catch(() => {});
    } else {
      document.exitFullscreen?.().catch(() => {});
    }
  };

  const toggleSound = () => {
    ensureAudio();
    setFeedSound((s) => !s);
  };

  const maxC = Math.max(...countries.map((c) => c.v));

  return (
    <section
      id="securitate"
      ref={sectionRef}
      data-testid="attackmap-section"
      className={`relative overflow-hidden border-t border-white/10 bg-ink-900 ${isFull ? "overflow-y-auto" : ""}`}
    >
      <div className="pointer-events-none absolute left-1/2 top-0 h-[40rem] w-[40rem] -translate-x-1/2 rounded-full bg-[#ff3b6b]/8 blur-[160px]" />
      <div className="pointer-events-none absolute inset-0 grid-lines opacity-40" />

      {/* Tur automat — titlu mare pe ecran (mod prezentare) */}
      {isFull && tourCity && (
        <div className="pointer-events-none fixed inset-x-0 bottom-8 z-[45] flex justify-center px-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={tourCity.city}
              initial={{ opacity: 0, y: 24, filter: "blur(8px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: 12, filter: "blur(8px)" }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="rounded-2xl border border-[#ff3b6b]/30 bg-ink-900/80 px-8 py-5 text-center backdrop-blur-xl"
            >
              <div className="font-mono text-[11px] uppercase tracking-[0.4em] text-[#ff7a97]">{t("am.detected")}</div>
              <div className="mt-2 font-display text-4xl font-extrabold leading-none text-white sm:text-6xl">
                <span className="mr-3">{tourCity.flag}</span>{tourCity.city}
              </div>
              <div className="mt-3 font-mono text-xs uppercase tracking-[0.2em] text-white/55">
                {lang === "en" ? tourCity.country_en : tourCity.country} · {tourCity.ip} · {tourCity.isp} · <span className="text-[#ff7a97]">{tourCity.atk}</span> · <span className="text-emerald-300">{t("am.blocked")}</span>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      )}

      <div className="relative mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
        <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
          <div className="max-w-2xl">
            <Reveal><SectionLabel index="[ • ]">{t("am.label")}</SectionLabel></Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
                {t("am.h1")} <span className="text-aurora">{t("am.hkw")}</span>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 max-w-xl font-body text-base leading-relaxed text-white/55">
                {t("am.sub")}
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <div className="flex items-center gap-3">
              <button
                data-testid="present-toggle"
                onClick={togglePresent}
                className="inline-flex items-center gap-2 rounded-lg border border-white/12 bg-ink-800/80 px-4 py-2.5 font-mono text-[11px] font-bold uppercase tracking-[0.12em] text-white/80 backdrop-blur-md transition-colors duration-200 hover:border-cyan-neon/60 hover:text-cyan-neon"
              >
                {isFull ? <Minimize size={15} /> : <Maximize size={15} />}
                {isFull ? t("am.exit") : t("am.present")}
              </button>
              <LiveDot />
            </div>
          </Reveal>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-2 lg:items-stretch">
          {/* 3D globe */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="card-edge relative overflow-hidden rounded-2xl"
          >
            <div className="absolute left-4 top-4 z-10 flex items-center gap-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">Live attack map</span>
              <span className="hidden font-mono text-[9px] uppercase tracking-wider text-cyan-neon/70 sm:inline">{t("am.hover")}</span>
            </div>
            <LiveFeed
              paused={feedPaused}
              soundOn={feedSound}
              onTogglePause={() => setFeedPaused((p) => !p)}
              onToggleSound={toggleSound}
              audioRef={audioRef}
            />
            <div className="h-[360px] w-full sm:h-[440px] lg:h-full lg:min-h-[520px]">
              <Globe tour={isFull} onTourChange={setTourCity} />
            </div>
            <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-between p-4">
              <div className="rounded-lg border border-white/10 bg-ink-900/70 px-3 py-2 backdrop-blur-md">
                <div className="font-mono text-[9px] uppercase tracking-widest text-white/40">{t("am.central")}</div>
                <div className="font-display text-sm font-bold text-cyan-neon">HQ · Bucureşti</div>
              </div>
              <div className="rounded-lg border border-white/10 bg-ink-900/70 px-3 py-2 backdrop-blur-md">
                <div className="font-mono text-[9px] uppercase tracking-widest text-white/40">{t("am.vectors")}</div>
                <div className="font-display text-sm font-bold text-[#ff3b6b]">{threats} {t("am.threats")}</div>
              </div>
            </div>
          </motion.div>

          {/* pfSense panels */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Panel title={t("am.p.blocked")} icon={Ban} accent="#ff3b6b" right={<LiveDot />}>
              <div className="flex items-end gap-2">
                <span className="font-display text-4xl font-extrabold text-white tabular-nums">
                  {blocked.toLocaleString("ro-RO")}
                </span>
              </div>
              <div className="mt-2 font-mono text-[11px] text-emerald-300">↑ +{(blocked % 97) + 3} {t("am.lasthour")}</div>
            </Panel>

            <Panel title={t("am.p.ids")} icon={Radar} accent="#00E5FF">
              <div className="flex flex-col gap-2">
                {[
                  { k: t("am.ids.rules"), v: "34.812" },
                  { k: t("am.ids.packets"), v: (12000 + (blocked % 500)).toLocaleString("ro-RO") },
                  { k: t("am.ids.state"), v: t("am.ids.protected"), ok: true },
                ].map((r) => (
                  <div key={r.k} className="flex items-center justify-between">
                    <span className="font-mono text-[11px] uppercase tracking-wider text-white/45">{r.k}</span>
                    <span className={`font-display text-sm font-bold ${r.ok ? "text-emerald-400" : "text-white"}`}>{r.v}</span>
                  </div>
                ))}
              </div>
            </Panel>

            <Panel title={t("am.p.countries")} icon={GlobeIcon} accent="#7C5CFC">
              <div className="flex flex-col gap-2.5">
                {countries.map((c) => (
                  <div key={c.code}>
                    <div className="mb-1 flex items-center justify-between font-mono text-[11px]">
                      <span className="text-white/70">{lang === "en" ? c.c_en : c.c} <span className="text-white/30">{c.code}</span></span>
                      <span className="text-white/50">{c.v.toFixed(0)}%</span>
                    </div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-white/8">
                      <motion.div
                        className="h-full rounded-full"
                        style={{ background: "linear-gradient(90deg,#ff3b6b,#7C5CFC)" }}
                        animate={{ width: `${(c.v / maxC) * 100}%` }}
                        transition={{ duration: 1.2, ease: "easeOut" }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </Panel>

            <Panel title={t("am.p.ports")} icon={ShieldAlert} accent="#f59e0b">
              <div className="flex flex-col divide-y divide-white/5">
                {ports.map((p) => (
                  <div key={p.p} className="flex items-center justify-between py-2">
                    <div className="flex items-center gap-2">
                      <span className="rounded bg-white/8 px-2 py-0.5 font-mono text-[11px] text-white">{p.p}</span>
                      <span className="font-mono text-[10px] uppercase tracking-wider text-white/40">{p.s}</span>
                    </div>
                    <span className="font-display text-sm font-bold text-[#ff3b6b] tabular-nums">{p.n}</span>
                  </div>
                ))}
              </div>
            </Panel>
          </div>
        </div>
      </div>
    </section>
  );
};
